import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';

import { DateTimePicker, TimePicker } from '@mui/x-date-pickers';
import ClearableSelect from 'components/ClearableSelect/ClearableSelect';

import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { FieldTemplateFormProps } from '.';
import AliasInput from 'components/AliasInput/AliasInput';
import {
  DEFAULT_VALUE_OPTION_VISIT_DATE,
  DateFieldDateTypes,
  getDatePickerViews,
} from '../utils';
import { getDateFormat } from 'routes/FormTemplateEditor/FormEditor/FieldTemplate/Fields';

const DEFAULT_VALUE_OPTION_VALUE = 'value';

interface Option {
  value: string;
  label: string;
}

export function DateFieldTemplate({
  formik,
  disabled,
  isRepeatingFieldsEnabled,
}: FieldTemplateFormProps) {
  const { t } = useTranslation();

  const DATE_TYPES = [
    {
      value: DateFieldDateTypes.Year,
      label: t('forms.fieldAttributes.dateTypes.year'),
    },
    {
      value: DateFieldDateTypes.Time,
      label: t('forms.fieldAttributes.dateTypes.hour-minute'),
    },
    {
      value: DateFieldDateTypes.Date,
      label: t('forms.fieldAttributes.dateTypes.year-month-day'),
    },
    {
      value: DateFieldDateTypes.DateTime,
      label: t('forms.fieldAttributes.dateTypes.year-month-day-hour-minute'),
    },
  ] as const;

  const DEFAULT_VALUE_OPTIONS = [
    {
      value: DEFAULT_VALUE_OPTION_VISIT_DATE,
      label: t('forms.fieldAttributes.defaultValueOptions.currentDate'),
    },
    {
      value: DEFAULT_VALUE_OPTION_VALUE,
      label: t('forms.fieldAttributes.defaultValueOptions.customValue'),
    },
  ] as const;

  return (
    <form>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            disabled={disabled}
            sx={{ mt: 1 }}
            fullWidth
            label={`${t('forms.fieldAttributes.label')} *`}
            type="text"
            name="label"
            value={formik.values.label}
            onChange={formik.handleChange}
            error={formik.touched.label && Boolean(formik.errors.label)}
            InputLabelProps={{
              shrink: true,
            }}
            helperText={formik.touched.label && formik.errors.label}
          />
        </Grid>

        <Grid item xs={6}>
          <AliasInput
            disabled={disabled}
            sx={{ mt: 1 }}
            type="text"
            name="alias"
            value={formik.values.alias}
            onChange={formik.handleChange}
            fullWidth
            error={formik.touched.alias && Boolean(formik.errors.alias)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>

        <Grid item xs={3} display={'flex'} alignItems={'center'}>
          <FormControlLabel
            disabled={disabled}
            control={<Checkbox />}
            label={`${t('forms.fieldAttributes.isRequired')}`}
            checked={formik.values.isRequired}
            onChange={() => {
              formik.setFieldValue('isRequired', !formik.values.isRequired);
            }}
          />
        </Grid>

        <Grid item xs={3} display={'flex'} alignItems={'center'}>
          {isRepeatingFieldsEnabled && (
            <FormControlLabel
              disabled={disabled}
              control={<Checkbox />}
              label={`${t('forms.fieldAttributes.isRepeatable')}`}
              checked={formik.values.isRepeatable}
              onChange={() => {
                formik.setFieldValue(
                  'isRepeatable',
                  !formik.values.isRepeatable,
                );
              }}
            />
          )}
        </Grid>

        <Grid item xs={6}></Grid>

        <Grid item xs={6}>
          <ClearableSelect
            disabled={disabled}
            sx={{ mt: 1 }}
            fullWidth
            onClear={() =>
              formik.setFieldValue(
                'dateType',
                formik.initialValues.dateType || '',
              )
            }
            label={t('forms.fieldAttributes.dateType')}
            defaultValue={DATE_TYPES[2].value}
            name="dateType"
            value={formik.values.dateType}
            onChange={e => formik.setFieldValue('dateType', e.target.value)}
          >
            {DATE_TYPES.map((option: Option) => (
              <MenuItem
                key={`date-type-list-${option.value}-${option.label}`}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </ClearableSelect>
        </Grid>

        <Grid item xs={6}>
          <ClearableSelect
            disabled={disabled}
            sx={{ mt: 1 }}
            fullWidth
            isClearable={true}
            onClear={() => {
              formik.setFieldValue('defaultValueType', undefined);
              formik.setFieldValue('defaultValue', null);
            }}
            label={t('forms.fieldAttributes.defaultValueType')}
            value={formik.values.defaultValueType}
            onChange={e => {
              formik.setFieldValue('defaultValueType', e.target.value);
              formik.setFieldValue('defaultValue', null);
            }}
          >
            {DEFAULT_VALUE_OPTIONS.map((option: Option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </ClearableSelect>
        </Grid>

        {formik.values.defaultValueType === DEFAULT_VALUE_OPTION_VALUE && (
          <Grid item xs={12}>
            {formik.values.dateType !== DateFieldDateTypes.Time ? (
              <DateTimePicker
                disabled={disabled}
                sx={{ mt: 1 }}
                ampm={false}
                value={moment(formik.values.defaultValue)}
                label={t('forms.fieldAttributes.defaultValue')}
                onChange={date => {
                  formik.setFieldValue('defaultValue', date?.toDate());
                }}
                format={getDateFormat(formik.values.dateType)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText:
                      formik.touched.defaultValue && formik.errors.defaultValue,
                    error:
                      formik.touched.defaultValue &&
                      Boolean(formik.errors.defaultValue),
                    name: 'defaultValue',
                    InputLabelProps: {
                      shrink: true,
                    },
                  },
                }}
                minutesStep={1}
                views={getDatePickerViews(formik.values.dateType)}
              />
            ) : (
              <TimePicker
                disabled={disabled}
                sx={{ mt: 1 }}
                ampm={false}
                value={moment(formik.values.defaultValue)}
                label={t('forms.fieldAttributes.defaultValue')}
                onChange={date => {
                  formik.setFieldValue('defaultValue', date?.toDate());
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText:
                      formik.touched.defaultValue && formik.errors.defaultValue,
                    error:
                      formik.touched.defaultValue &&
                      Boolean(formik.errors.defaultValue),
                    name: 'defaultValue',
                    InputLabelProps: {
                      shrink: true,
                    },
                  },
                }}
                minutesStep={1}
              />
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <FormLabel>{t('forms.validationRules')}</FormLabel>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={disabled}
                  onChange={e => {
                    const target = e.target as HTMLInputElement;
                    const formikProp = target.id as keyof typeof formik.values;
                    formik.setFieldValue(
                      formikProp,
                      !formik.values[formikProp as keyof typeof formik.values],
                    );
                  }}
                  checked={formik.values.cantBeEarlierThanBirthDate}
                  id="cantBeEarlierThanBirthDate"
                />
              }
              label={t('forms.fieldAttributes.cantBeEarlierThanBirthDate')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  disabled={disabled}
                  onChange={e => {
                    const target = e.target as HTMLInputElement;
                    const formikProp = target.id as keyof typeof formik.values;
                    formik.setFieldValue(
                      formikProp,
                      !formik.values[formikProp as keyof typeof formik.values],
                    );
                  }}
                  checked={formik.values.cantBeLaterThanVisitDate}
                  id="cantBeLaterThanVisitDate"
                />
              }
              label={t('forms.fieldAttributes.cantBeLaterThanVisitDate')}
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12}>
          <TextField
            disabled={disabled}
            sx={{ mt: 1 }}
            fullWidth
            label={t('forms.fieldAttributes.description')}
            type="text"
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            multiline
            rows={3}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
      </Grid>
    </form>
  );
}
